import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'
import {
  fieldBaseClasses,
  fieldDescribedBy,
  fieldErrorClasses,
  labelStyles,
} from './form-field-styles'
import { FieldMessages } from './field-messages'

/**
 * Input component variants using class-variance-authority.
 * Base look lives in `form-field-styles.ts` so Textarea and the other field
 * primitives match (16px upright placeholder, no focus ring, tokens only).
 */
const inputVariants = cva(fieldBaseClasses, {
  variants: {
    inputSize: {
      default: 'h-11 px-3.5',
      // Compact rows (toolbars, tables): shorter, but text stays at the 16px minimum.
      sm: 'h-9 px-3',
      lg: 'h-12 px-4 text-lg',
    },
    hasError: {
      true: fieldErrorClasses,
      false: '',
    },
  },
  defaultVariants: {
    inputSize: 'default',
    hasError: false,
  },
})

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof inputVariants> {
  /** Label text displayed above the input */
  label?: string
  /** Helper text displayed below the input */
  helperText?: string
  /** Error message - when provided, input shows error state */
  error?: string
  /** Left addon element (icon or text) */
  leftAddon?: React.ReactNode
  /** Right addon element (icon or text) */
  rightAddon?: React.ReactNode
  /** Container className for wrapper div */
  containerClassName?: string
}

/**
 * Input component with label, helper text, and error states
 */
const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      containerClassName,
      inputSize,
      label,
      helperText,
      error,
      leftAddon,
      rightAddon,
      id,
      disabled,
      ...props
    },
    ref
  ) => {
    // Generate unique ID if not provided
    const generatedId = React.useId()
    const inputId = id || generatedId
    const hasError = !!error

    return (
      <div className={cn('w-full', containerClassName)}>
        {/* Label */}
        {label && (
          <label htmlFor={inputId} className={labelStyles}>
            {label}
          </label>
        )}

        {/* Input wrapper for addons */}
        <div className="relative">
          {/* Left addon */}
          {leftAddon && (
            // Decorative ($, search icon): clicks fall through to the field.
            <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-clovie-muted">
              {leftAddon}
            </div>
          )}

          {/* Input element */}
          <input
            id={inputId}
            ref={ref}
            disabled={disabled}
            className={cn(
              inputVariants({ inputSize, hasError }),
              leftAddon && 'pl-10',
              rightAddon && 'pr-10',
              className
            )}
            data-field=""
            aria-invalid={hasError}
            aria-describedby={fieldDescribedBy(inputId, { error, helperText })}
            {...props}
          />

          {/* Right addon */}
          {rightAddon && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-clovie-muted">
              {rightAddon}
            </div>
          )}
        </div>

        <FieldMessages fieldId={inputId} error={error} helperText={helperText} />
      </div>
    )
  }
)
Input.displayName = 'Input'

export { Input, inputVariants }
// Re-exported for the other field primitives that import them from here.
export { labelStyles, helperTextStyles, errorTextStyles } from './form-field-styles'
